/**
 * HeaderSection — Horizontal header bar.
 * Layout: [Logo — left] | [Newsletter Title — center] | [Subtitle/Date — right]
 * Optional full-width banner image below.
 */
import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { ImageIcon } from 'lucide-react';
import { API_URL } from '../../../api/api';

export function HeaderSection({ section, isSelected, updateSection }) {
  const data = section.data || {};
  const st = section.settings || {};
  const logoRef = useRef(null);

  const title = data.title ?? 'Newsletter Title';
  const subtitle = data.subtitle ?? '';
  const logoUrl = data.logoUrl ?? null;
  const bannerUrl = data.bannerImageUrl ?? null;

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const updateSettings = (patch) =>
    updateSection(section.id, { settings: { ...st, ...patch } });

  // Migrate old Georgia/26 defaults to Instrument Serif/28
  // Also strip the now-redundant subtextColor override so the subtitle
  // follows the main textColor correctly.
  useEffect(() => {
    const patch = {};
    if (st.fontFamily === 'Georgia, serif') patch.fontFamily = "'Instrument Serif', serif";
    if (st.titleSize === 26) patch.titleSize = 28;
    if (st.subtitleSize === 13) patch.subtitleSize = 14;
    // Remove stale subtextColor so subtitle always follows textColor.
    if ('subtextColor' in st) patch.subtextColor = undefined;
    if (Object.keys(patch).length > 0) updateSettings(patch);
  }, []); // eslint-disable-line

  // Auto-fetch company logo + name on first add
  useEffect(() => {
    if (data.logoFetched) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${API_URL}/api/settings/company-info`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        updateData({
          logoUrl: r.data.company_logo ? `${API_URL}${r.data.company_logo}` : null,
          logoFetched: true,
        });
      })
      .catch(() => updateData({ logoFetched: true }));
  }, []); // eslint-disable-line

  const bg = st.backgroundColor || '#f4efe5';
  const fontCol = st.textColor || '#000000';
  const padding = st.padding || '14px 24px';

  return (
    <div style={{ backgroundColor: bg }}>
      {/* ── Horizontal header bar ─────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding,
        gap: 16,
      }}>
        {/* Left — Logo */}
        <div
          style={{ flexShrink: 0, cursor: isSelected ? 'pointer' : 'default' }}
          onClick={() => isSelected && logoRef.current?.click()}
          title={isSelected ? 'Click to replace logo' : undefined}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              style={{ height: 44, maxWidth: 120, objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <div style={{
              width: 80, height: 36, borderRadius: 4,
              background: '#e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: '#9ca3af',
            }}>
              {isSelected ? 'Click for logo' : 'No logo'}
            </div>
          )}
          <input
            ref={logoRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const form = new FormData();
              form.append('file', file);
              const token = localStorage.getItem('token');
              try {
                const r = await axios.post(`${API_URL}/api/newsletters/assets/upload`, form, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                updateData({ logoUrl: `${API_URL}${r.data.url}` });
              } catch { /* silent */ }
              e.target.value = '';
            }}
          />
        </div>

        {/* Center — Title */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          {isSelected ? (
            <input
              value={title}
              onChange={(e) => updateData({ title: e.target.value })}
              placeholder="Newsletter Title"
              style={{
                width: '100%', border: 'none', background: 'transparent',
                textAlign: 'center', outline: 'none',
                fontSize: st.titleSize || 28,
                fontWeight: 700,
                fontFamily: "'Instrument Serif', serif",
                color: fontCol,
                borderBottom: '1px dashed #d1d5db',
              }}
            />
          ) : (
            <span style={{
              fontSize: st.titleSize || 28,
              fontWeight: 700,
              fontFamily: "'Instrument Serif', serif",
              color: fontCol,
            }}>
              {title}
            </span>
          )}
        </div>

        {/* Right — Subtitle / Date */}
        <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 80 }}>
          {isSelected ? (
            <input
              value={subtitle}
              onChange={(e) => updateData({ subtitle: e.target.value })}
              placeholder="April 2026"
              style={{
                border: 'none', background: 'transparent', outline: 'none',
                textAlign: 'right', width: 110,
                fontSize: st.subtitleSize || 14,
                color: fontCol,
                borderBottom: '1px dashed #d1d5db',
              }}
            />
          ) : (
            subtitle && (
              <span style={{
                fontSize: st.subtitleSize || 14,
                color: fontCol,
              }}>
                {subtitle}
              </span>
            )
          )}
        </div>
      </div>

      {/* ── Optional banner image ──────────────────────────────── */}
      {bannerUrl ? (
        <div style={{ position: 'relative' }}>
          <img
            src={bannerUrl}
            alt="Banner"
            style={{ width: '100%', height: st.bannerHeight || 120, objectFit: 'cover', display: 'block' }}
          />
          {isSelected && (
            <button
              onClick={() => updateData({ bannerImageUrl: null })}
              style={{
                position: 'absolute', top: 8, right: 8,
                background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: 4,
                color: '#fff', fontSize: 11, padding: '3px 8px', cursor: 'pointer',
              }}
            >
              Remove banner
            </button>
          )}
        </div>
      ) : (
        isSelected && (
          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 6, padding: '18px 0', cursor: 'pointer',
            border: '1.5px dashed #d1d5db', borderRadius: 0,
            background: '#fafafa', color: '#9ca3af', fontSize: 12,
          }}>
            <ImageIcon size={20} color="#d1d5db" />
            Drag &amp; drop or <span style={{ color: '#f54b01', fontWeight: 600 }}>click to upload</span> banner
            <span style={{ fontSize: 11, color: '#d1d5db' }}>Recommended: 700×200</span>
            <input
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const form = new FormData();
                form.append('file', file);
                const token = localStorage.getItem('token');
                try {
                  const r = await axios.post(`${API_URL}/api/newsletters/assets/upload`, form, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  updateData({ bannerImageUrl: `${API_URL}${r.data.url}` });
                } catch { /* silent */ }
                e.target.value = '';
              }}
            />
          </label>
        )
      )}
    </div>
  );
}
